import { createI18n } from "vue-i18n";
import { guard } from "@/composables/useSafeI18n";
import { en as vuetifyEn, fr as vuetifyFr } from "vuetify/locale";

const messages = {
  en: {
    $vuetify: vuetifyEn,
    delete: "Delete",
    inputPlaceholder: "Click to fill in",
    savedOnDevice:
      "Network unavailable: changes saved on this device, they will be sent automatically once the network is back.",
    pendingChangeRejected:
      "A change made on {date}{where} during a network cut was rejected by the server. It is kept on this device: open the page where it was made to fix it, or discard it.",
    pendingChangeRejectedDetail:
      "A change made on {date}{where} during a network cut was rejected by the server: “{detail}”. It is kept on this device: open the page where it was made to fix it, or discard it.",
    pendingChangeSendFailed:
      "A change made on {date}{where} could not be sent: technical error. It is kept on this device and sending will be retried automatically.",
    offlineWithStoredData:
      "Data already on this device (loaded on {date} at {time}): you are now in offline mode.",
    syncNeedsLogin:
      "Session expired: log in again to send the changes saved on this device. They stay on the device meanwhile.",
    deviceStorageFull:
      "the device storage is full, nothing was saved. Export the waiting changes for support (cloud menu), then free some space",
    openPage: "Open the page",
    onMission: "on mission “{mission}”",
    discardChange: "Discard the change",
    discardChangeTitle: "Discard the change?",
    discardChangeText:
      "The change kept on this device will be dropped: the item goes back to the version saved on the server, or is removed if the server never got it.",
    keepChange: "Keep the change",
    changeDiscarded: "Change discarded.",
    changeNotDiscarded:
      "The change could not be discarded. Try again once the network is back.",
    pageUnavailableOffline:
      "This screen is not available offline yet: try again once the network is back.",
    onlyOfflineMission:
      "Offline mode: only the prepared mission is available. Go back online to open the other pages.",
    actionNeedsNetwork:
      "This action requires a connection. Try again once you have network.",
    actionNeedsOnlineMode:
      "This action is not available in offline mode: go back online to use it.",
    goOnline: "Go back online",
    lastServerReach: "Last connection: {date} at {time}",
    lastServerReachUnknown: "Last connection: unknown",
    backOnline: "Back online.",
    syncDone: "Back online: changes saved on this device have been sent.",
    syncNowDone: "Changes saved on this device have been sent.",
    syncInOtherTab:
      "A synchronization is already running in another tab of the application.",
    syncIncomplete:
      "Network unavailable: some changes are still on this device, they will be sent automatically.",
    recordBookSyncFailed:
      "Record book « {label} »{where} could not be synchronized. It is kept on this device: open its page to fix it, or discard the change.",
    recordBookSyncFailedDetail:
      "Record book « {label} »{where} could not be synchronized: “{detail}”. It is kept on this device: open its page to fix it, or discard the change.",
    recordBookSendFailed:
      "Record book « {label} »{where} could not be sent: technical error. It is kept on this device and sending will be retried automatically.",
    waitingNotifications:
      "{count} other notification waiting | {count} other notifications waiting",
    obsoletePhaseRecordBooks:
      "Automatic selection failed for the following sampling record book(s) linked to an obsolete sampling phase: {names}. Please archive them.",
  },
  fr: {
    $vuetify: vuetifyFr,
    delete: "Supprimer",
    inputPlaceholder: "Cliquez pour renseigner",
    savedOnDevice:
      "Réseau indisponible : modifications enregistrées sur l'appareil, elles seront envoyées automatiquement au retour du réseau.",
    pendingChangeRejected:
      "Une modification faite le {date}{where} pendant une coupure réseau a été refusée par le serveur. Elle est conservée sur l'appareil : ouvrez la page où elle a été faite pour la corriger, ou annulez-la.",
    pendingChangeRejectedDetail:
      "Une modification faite le {date}{where} pendant une coupure réseau a été refusée par le serveur : « {detail} ». Elle est conservée sur l'appareil : ouvrez la page où elle a été faite pour la corriger, ou annulez-la.",
    pendingChangeSendFailed:
      "Une modification faite le {date}{where} n'a pas pu être envoyée : erreur technique. Elle est conservée sur l'appareil et l'envoi sera retenté automatiquement.",
    offlineWithStoredData:
      "Données déjà sur l'appareil (chargées le {date} à {time}) : vous êtes en mode hors ligne.",
    syncNeedsLogin:
      "Session expirée : reconnectez-vous pour envoyer les modifications enregistrées sur l'appareil. Elles y restent en attendant.",
    deviceStorageFull:
      "le stockage de l'appareil est plein, rien n'a été enregistré. Exportez les modifications en attente pour le support (menu du nuage), puis libérez de l'espace",
    openPage: "Ouvrir la page",
    onMission: "sur la mission « {mission} »",
    discardChange: "Annuler le changement",
    discardChangeTitle: "Annuler le changement ?",
    discardChangeText:
      "La modification conservée sur l'appareil sera abandonnée : l'élément reprend sa version enregistrée sur le serveur, ou disparaît s'il n'y a jamais été enregistré.",
    keepChange: "Garder la modification",
    changeDiscarded: "Modification annulée.",
    changeNotDiscarded:
      "La modification n'a pas pu être annulée. Réessayez au retour du réseau.",
    pageUnavailableOffline:
      "Cet écran n'est pas disponible hors ligne pour le moment : réessayez au retour du réseau.",
    onlyOfflineMission:
      "Mode hors ligne : seule la mission préparée est accessible. Repassez en ligne pour ouvrir les autres pages.",
    actionNeedsNetwork:
      "Cette action nécessite une connexion. Réessayez lorsque vous aurez du réseau.",
    actionNeedsOnlineMode:
      "Action indisponible en mode hors ligne : repassez en ligne pour l'utiliser.",
    goOnline: "Repasser en ligne",
    lastServerReach: "Dernière connexion : {date} à {time}",
    lastServerReachUnknown: "Dernière connexion : inconnue",
    backOnline: "Vous êtes de nouveau en ligne.",
    syncDone:
      "De nouveau en ligne : les modifications enregistrées sur l'appareil ont été envoyées.",
    syncNowDone:
      "Les modifications enregistrées sur l'appareil ont été envoyées.",
    syncInOtherTab:
      "Une synchronisation est déjà en cours dans un autre onglet de l'application.",
    syncIncomplete:
      "Réseau indisponible : des modifications restent sur l'appareil, elles seront envoyées automatiquement.",
    recordBookSyncFailed:
      "Le cahier « {label} »{where} n'a pas pu être synchronisé. Il reste enregistré sur cet appareil : ouvrez sa page pour le corriger, ou annulez le changement.",
    recordBookSyncFailedDetail:
      "Le cahier « {label} »{where} n'a pas pu être synchronisé : « {detail} ». Il reste enregistré sur cet appareil : ouvrez sa page pour le corriger, ou annulez le changement.",
    recordBookSendFailed:
      "Le cahier « {label} »{where} n'a pas pu être envoyé : erreur technique. Il reste enregistré sur cet appareil et l'envoi sera retenté automatiquement.",
    waitingNotifications:
      "{count} autre notification en attente | {count} autres notifications en attente",
    obsoletePhaseRecordBooks:
      "Sélection automatique impossible pour le(s) cahier(s) de prélèvement suivant(s), lié(s) à une phase de prélèvement obsolète : {names}. Merci de les archiver.",
  },
};

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: import.meta.env.VITE_APP_I18N_LOCALE || "en",
  fallbackLocale: import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE || "en",
  messages,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false,
});

const rawGlobalT = i18n.global.t.bind(i18n.global);
i18n.global.t = guard(rawGlobalT);

export const i18nGlobal = i18n.global;

export default i18n;
